import { useCallback } from 'react';
import { ContactDetailsType, VerificationType } from '@brighte/validation';
import { AuthenticationFlowType, useCreatePasswordlessUserMutation } from '../graphql';
import { useAuth0 } from '../provider';

export type PasswordlessVerificationDetails = Pick<ContactDetailsType, 'verificationType' | 'mobile' | 'email' | 'otp'>;

export type CreatePasswordlessUserType = Pick<
  ContactDetailsType,
  'verificationType' | 'mobile' | 'email' | 'financialProductId'
>;

export const usePasswordlessUserVerification = () => {
  const { user, authorize, error, isLoading, clearAuthErrors } = useAuth0();
  const {
    data: createPasswordlessUserResponse,
    isLoading: createPasswordlessUserLoading,
    error: errorCreatePasswordlessUser,
    mutate: createPasswordlessUserMutation,
  } = useCreatePasswordlessUserMutation();

  const createPasswordlessUser = useCallback(
    ({ mobile, email, verificationType, financialProductId }: CreatePasswordlessUserType) => {
      clearAuthErrors();
      createPasswordlessUserMutation({
        input: {
          mobile,
          email,
          financialProduct: financialProductId,
          // if both email and mobile are same in backend we just do SMS
          type:
            verificationType === VerificationType.Sms || verificationType === VerificationType.SmsEmail
              ? AuthenticationFlowType.SMS
              : AuthenticationFlowType.EMAIL,
        },
      });
    },
    [],
  );

  const authenticatePasswordlessUser = useCallback(
    ({ verificationType, mobile, email, otp }: PasswordlessVerificationDetails) => {
      switch (true) {
        // we need to verify the other type of verificationType - if we have both mobile and email we do passwordless sms so we can get token
        case otp && (verificationType === VerificationType.Sms || verificationType === VerificationType.SmsEmail):
          authorize({ type: 'sms', code: otp as string, mobile });
          break;
        case otp && verificationType === VerificationType.Email && !!email:
          authorize({ type: 'email', code: otp as string, email });
          break;
        default:
          break;
      }
    },
    [],
  );

  return {
    // Create passwordless user
    createPasswordlessUser,
    createPasswordlessUserResponse,
    createPasswordlessUserLoading,
    errorCreatePasswordlessUser,

    // Authenticate passwordless user
    authenticatePasswordlessUser,
    authenticatePasswordlessUserResponse: user,
    authenticatePasswordlessLoading: isLoading,
    authenticatePasswordlessUserError: error,
  };
};
